import { useState, useMemo } from "react";
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  X,
  AlertTriangle,
  Bug,
  Shield,
} from "lucide-react";
import { Button, Badge } from "@asm/ui";
import type { ScoredFinding, RiskLevel } from "@/lib/api";
import { RiskBadge, RiskDot } from "./RiskBadge";
import { ScorePill } from "./ScoreGauge";
import { EpssIndicator } from "./EpssScore";
import { FindingCard } from "./FindingCard";

interface FindingsListProps {
  findings: ScoredFinding[];
  className?: string;
}

type SortField = "rank" | "score" | "risk_level" | "module";
type SortDirection = "asc" | "desc";

const RISK_LEVEL_ORDER: Record<RiskLevel, number> = {
  critical: 5,
  high: 4,
  medium: 3,
  low: 2,
  info: 1,
};

// Format module name: remove underscores, uppercase
function formatModuleName(name: string): string {
  return name.replace(/_/g, " ").toUpperCase();
}

function FilterChip({
  label,
  active,
  onClick,
  color = "default",
}: {
  label: React.ReactNode;
  active: boolean;
  onClick: () => void;
  color?: "default" | "red" | "orange" | "amber" | "cyan" | "slate";
}) {
  const colors = {
    default: active
      ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/40"
      : "bg-muted/30 text-muted-foreground border-border/50 hover:border-cyan-500/30",
    red: active
      ? "bg-red-500/20 text-red-400 border-red-500/40"
      : "bg-muted/30 text-muted-foreground border-border/50 hover:border-red-500/30",
    orange: active
      ? "bg-orange-500/20 text-orange-400 border-orange-500/40"
      : "bg-muted/30 text-muted-foreground border-border/50 hover:border-orange-500/30",
    amber: active
      ? "bg-amber-500/20 text-amber-400 border-amber-500/40"
      : "bg-muted/30 text-muted-foreground border-border/50 hover:border-amber-500/30",
    cyan: active
      ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/40"
      : "bg-muted/30 text-muted-foreground border-border/50 hover:border-cyan-500/30",
    slate: active
      ? "bg-slate-500/20 text-slate-400 border-slate-500/40"
      : "bg-muted/30 text-muted-foreground border-border/50 hover:border-slate-500/30",
  };

  return (
    <button
      onClick={onClick}
      className={`
        px-2.5 py-1 rounded-full text-xs font-medium
        border transition-all duration-200
        ${colors[color]}
      `}
    >
      {label}
    </button>
  );
}

function SortHeader({
  label,
  field,
  currentField,
  direction,
  onSort,
  className = "",
}: {
  label: string;
  field: SortField;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  className?: string;
}) {
  const isActive = currentField === field;

  return (
    <button
      onClick={() => onSort(field)}
      className={`
        flex items-center gap-1 text-xs font-medium uppercase tracking-wider
        text-muted-foreground hover:text-foreground transition-colors
        ${isActive ? "text-cyan-400" : ""}
        ${className}
      `}
    >
      {label}
      {isActive ? (
        direction === "asc" ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-50" />
      )}
    </button>
  );
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function FindingsList({ findings, className = "" }: FindingsListProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRiskLevels, setSelectedRiskLevels] = useState<Set<RiskLevel>>(
    new Set()
  );
  const [selectedModules, setSelectedModules] = useState<Set<string>>(
    new Set()
  );
  const [showCveOnly, setShowCveOnly] = useState(false);
  const [showKevOnly, setShowKevOnly] = useState(false);
  const [sortField, setSortField] = useState<SortField>("rank");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Get unique modules
  const modules = useMemo(() => {
    const moduleSet = new Set(findings.map((f) => f.source_module));
    return Array.from(moduleSet).sort();
  }, [findings]);

  // Filter and sort findings
  const filteredFindings = useMemo(() => {
    let result = [...findings];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (f) =>
          f.title.toLowerCase().includes(query) ||
          f.description.toLowerCase().includes(query) ||
          f.source_module.toLowerCase().includes(query) ||
          f.asset_id?.toLowerCase().includes(query) ||
          f.cve_ids.some((cve) => cve.toLowerCase().includes(query))
      );
    }

    // Risk level filter
    if (selectedRiskLevels.size > 0) {
      result = result.filter((f) =>
        selectedRiskLevels.has(f.score.risk_level)
      );
    }

    // Module filter
    if (selectedModules.size > 0) {
      result = result.filter((f) => selectedModules.has(f.source_module));
    }

    // CVE filter
    if (showCveOnly) {
      result = result.filter((f) => f.cve_ids.length > 0);
    }

    // KEV filter
    if (showKevOnly) {
      result = result.filter((f) => f.is_kev);
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "rank":
          comparison = a.rank - b.rank;
          break;
        case "score":
          comparison = a.score.score - b.score.score;
          break;
        case "risk_level":
          comparison =
            RISK_LEVEL_ORDER[a.score.risk_level] -
            RISK_LEVEL_ORDER[b.score.risk_level];
          break;
        case "module":
          comparison = a.source_module.localeCompare(b.source_module);
          break;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return result;
  }, [
    findings,
    searchQuery,
    selectedRiskLevels,
    selectedModules,
    showCveOnly,
    showKevOnly,
    sortField,
    sortDirection,
  ]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredFindings.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, filteredFindings.length);
  const paginatedFindings = filteredFindings.slice(startIndex, endIndex);

  // Reset to page 1 when filters or page size change
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Ensure current page is valid when filtered results change
  useMemo(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push("ellipsis");
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("ellipsis");
      }

      pages.push(totalPages);
    }

    return pages;
  };

  const toggleRiskLevel = (level: RiskLevel) => {
    const newSet = new Set(selectedRiskLevels);
    if (newSet.has(level)) {
      newSet.delete(level);
    } else {
      newSet.add(level);
    }
    setSelectedRiskLevels(newSet);
    setCurrentPage(1);
  };

  const toggleModule = (module: string) => {
    const newSet = new Set(selectedModules);
    if (newSet.has(module)) {
      newSet.delete(module);
    } else {
      newSet.add(module);
    }
    setSelectedModules(newSet);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedRiskLevels(new Set());
    setSelectedModules(new Set());
    setShowCveOnly(false);
    setShowKevOnly(false);
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(field === "score" ? "desc" : "asc");
    }
  };

  const hasFilters =
    searchQuery ||
    selectedRiskLevels.size > 0 ||
    selectedModules.size > 0 ||
    showCveOnly ||
    showKevOnly;

  return (
    <div className={`rounded-xl border border-border bg-card/50 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <Shield className="h-4 w-4 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-semibold">Security Findings</h3>
              <p className="text-xs text-muted-foreground">
                {filteredFindings.length} of {findings.length} findings
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search findings..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="
                w-full pl-9 pr-4 py-2 rounded-lg
                bg-muted/30 border border-border/50
                text-sm placeholder:text-muted-foreground
                focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20
                transition-all duration-200
              "
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Filter className="h-3.5 w-3.5" />
            Risk:
          </div>
          <div className="flex items-center gap-1.5">
            <FilterChip
              label="Critical"
              active={selectedRiskLevels.has("critical")}
              onClick={() => toggleRiskLevel("critical")}
              color="red"
            />
            <FilterChip
              label="High"
              active={selectedRiskLevels.has("high")}
              onClick={() => toggleRiskLevel("high")}
              color="orange"
            />
            <FilterChip
              label="Medium"
              active={selectedRiskLevels.has("medium")}
              onClick={() => toggleRiskLevel("medium")}
              color="amber"
            />
            <FilterChip
              label="Low"
              active={selectedRiskLevels.has("low")}
              onClick={() => toggleRiskLevel("low")}
              color="cyan"
            />
            <FilterChip
              label="Info"
              active={selectedRiskLevels.has("info")}
              onClick={() => toggleRiskLevel("info")}
              color="slate"
            />
          </div>

          <div className="h-4 w-px bg-border/50" />

          <FilterChip
            label={
              <>
                <Bug className="h-3 w-3 inline mr-1" />
                Has CVE
              </>
            }
            active={showCveOnly}
            onClick={() => {
              setShowCveOnly(!showCveOnly);
              setCurrentPage(1);
            }}
            color="amber"
          />

          <FilterChip
            label={
              <>
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                KEV Listed
              </>
            }
            active={showKevOnly}
            onClick={() => {
              setShowKevOnly(!showKevOnly);
              setCurrentPage(1);
            }}
            color="red"
          />

          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-7 px-2 text-xs gap-1 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
              Clear
            </Button>
          )}
        </div>

        {/* Module filters */}
        {modules.length > 1 && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/30">
            <span className="text-xs text-muted-foreground">Module:</span>
            <div className="flex items-center gap-1.5 flex-wrap">
              {modules.map((module) => (
                <FilterChip
                  key={module}
                  label={formatModuleName(module)}
                  active={selectedModules.has(module)}
                  onClick={() => toggleModule(module)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Table header */}
      <div className="px-4 py-2 border-b border-border/30 bg-muted/20">
        <div className="flex items-center gap-4">
          <SortHeader
            label="#"
            field="rank"
            currentField={sortField}
            direction={sortDirection}
            onSort={handleSort}
            className="w-8"
          />
          <div className="flex-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Finding
          </div>
          <SortHeader
            label="Score"
            field="score"
            currentField={sortField}
            direction={sortDirection}
            onSort={handleSort}
            className="w-20 justify-center"
          />
          <SortHeader
            label="Risk"
            field="risk_level"
            currentField={sortField}
            direction={sortDirection}
            onSort={handleSort}
            className="w-24 justify-center"
          />
          <SortHeader
            label="Module"
            field="module"
            currentField={sortField}
            direction={sortDirection}
            onSort={handleSort}
            className="w-28"
          />
          <div className="w-12 text-xs font-medium uppercase tracking-wider text-muted-foreground text-center">
            CVEs
          </div>
        </div>
      </div>

      {/* Findings list */}
      <div className="divide-y divide-border/30">
        {paginatedFindings.length === 0 ? (
          <div className="py-12 text-center">
            <div className="p-4 rounded-full bg-muted/30 inline-block mb-4">
              <Shield className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">
              {hasFilters
                ? "No findings match your filters"
                : "No security findings detected"}
            </p>
          </div>
        ) : (
          paginatedFindings.map((finding, index) => (
            <div
              key={finding.id}
              className="animate-fade-in-up"
              style={{ animationDelay: `${Math.min(index, 10) * 30}ms` }}
            >
              {expandedId === finding.id ? (
                <div className="p-4">
                  <FindingCard
                    finding={finding}
                    expanded={true}
                    className="border-0 bg-transparent"
                  />
                  <button
                    onClick={() => setExpandedId(null)}
                    className="mt-2 text-xs text-muted-foreground hover:text-cyan-400 transition-colors"
                  >
                    Collapse details
                  </button>
                </div>
              ) : (
                <div
                  className="
                    flex items-center gap-4 px-4 py-3
                    hover:bg-muted/30 cursor-pointer transition-colors
                  "
                  onClick={() => setExpandedId(finding.id)}
                >
                  <span className="w-8 text-center font-mono text-sm text-muted-foreground">
                    {finding.rank}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">
                        {finding.title}
                      </span>
                      {finding.is_kev && (
                        <span className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 text-[10px] font-semibold uppercase tracking-wider border border-red-500/30">
                          KEV
                        </span>
                      )}
                      {finding.epss && <EpssIndicator epss={finding.epss.epss} />}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                      {finding.asset_id && (
                        <span className="font-mono truncate max-w-[200px]">
                          {finding.asset_id}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-20 flex justify-center">
                    <ScorePill
                      score={finding.score.score}
                      riskLevel={finding.score.risk_level}
                    />
                  </div>
                  <div className="w-24 flex justify-center">
                    <RiskBadge level={finding.score.risk_level} size="sm" />
                  </div>
                  <div className="w-28">
                    <Badge variant="outline" className="text-xs">
                      {formatModuleName(finding.source_module)}
                    </Badge>
                  </div>
                  <div className="w-12 text-center text-xs">
                    {finding.cve_ids.length > 0 ? (
                      <span className="font-mono text-amber-400">
                        {finding.cve_ids.length}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer with pagination */}
      {filteredFindings.length > 0 && (
        <div className="px-4 py-3 border-t border-border/30 bg-muted/10">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            {/* Left: Page size selector and count */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <span>Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="
                    px-2 py-1 rounded-md
                    bg-muted/50 border border-border/50
                    text-xs text-foreground
                    focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20
                    cursor-pointer
                  "
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span>per page</span>
              </div>
              <span className="hidden sm:inline">|</span>
              <span className="hidden sm:inline">
                Showing {startIndex + 1}–{endIndex} of {filteredFindings.length}
                {filteredFindings.length !== findings.length && (
                  <span className="text-muted-foreground/60"> (filtered from {findings.length})</span>
                )}
              </span>
            </div>

            {/* Center: Risk summary (hidden on small screens) */}
            <div className="hidden lg:flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <RiskDot level="critical" />
                {filteredFindings.filter((f) => f.score.risk_level === "critical").length} Critical
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <RiskDot level="high" />
                {filteredFindings.filter((f) => f.score.risk_level === "high").length} High
              </span>
            </div>

            {/* Right: Pagination controls */}
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                {/* First page */}
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className={`
                    p-1.5 rounded-md transition-colors
                    ${currentPage === 1
                      ? "text-muted-foreground/40 cursor-not-allowed"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }
                  `}
                  title="First page"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>

                {/* Previous page */}
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`
                    p-1.5 rounded-md transition-colors
                    ${currentPage === 1
                      ? "text-muted-foreground/40 cursor-not-allowed"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }
                  `}
                  title="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-0.5 mx-1">
                  {getPageNumbers().map((page, idx) =>
                    page === "ellipsis" ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-2 text-xs text-muted-foreground/60"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`
                          min-w-[28px] h-7 px-2 rounded-md text-xs font-medium
                          transition-all duration-200
                          ${currentPage === page
                            ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                          }
                        `}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>

                {/* Next page */}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`
                    p-1.5 rounded-md transition-colors
                    ${currentPage === totalPages
                      ? "text-muted-foreground/40 cursor-not-allowed"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }
                  `}
                  title="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>

                {/* Last page */}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`
                    p-1.5 rounded-md transition-colors
                    ${currentPage === totalPages
                      ? "text-muted-foreground/40 cursor-not-allowed"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }
                  `}
                  title="Last page"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default FindingsList;
